import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { Pencil, Trash2 } from 'lucide-react';
import { Task } from '../../types';

interface TaskTemplateListProps {
  tasks: Task[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  isMobile: boolean;
}

export const TaskTemplateList: React.FC<TaskTemplateListProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  isMobile,
}) => {
  if (tasks.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No task templates defined for this stage
      </Typography>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={2}>
        {tasks.map((task) => (
          <Card key={task.id}>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                  {task.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {task.description}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: 1,
                borderTop: 1,
                borderColor: 'divider',
                pt: 2,
                mt: 2,
              }}>
                <IconButton
                  size="small"
                  onClick={() => onEditTask(task)}
                  color="primary"
                >
                  <Pencil size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDeleteTask(task.id)}
                  color="error"
                >
                  <Trash2 size={18} />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <List>
      {tasks.map((task, index) => (
        <React.Fragment key={task.id}>
          <ListItem
            secondaryAction={
              <Box>
                <IconButton
                  edge="end"
                  onClick={() => onEditTask(task)}
                  sx={{ mr: 1 }}
                >
                  <Pencil size={18} />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => onDeleteTask(task.id)}
                  color="error"
                >
                  <Trash2 size={18} />
                </IconButton>
              </Box>
            }
          >
            <ListItemText
              primary={task.title}
              secondary={task.description}
              primaryTypographyProps={{
                fontWeight: 500,
              }}
            />
          </ListItem>
          {index < tasks.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
};